<template>
  <div>
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="global-domains-data-table"
          with-filter
          :title="$t('Allowed & Blocked Domains')"
          :filter-placeholder="$t('Search Domains')"
          :loading="loading"
          :table-data="dataSet"
          :actions="tableActions"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @on-row-clicked="onRowClicked"
          @on-import="onImport()"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
        />
      </b-col>
    </b-row>
    <section>
      <t-uploader :dialog="modal" @submitFiles="submit"></t-uploader>
    </section>
  </div>
</template>

<route>
{
  "name": "System Global Domains"
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import TUploader from "@/components/TUploader";

export default {
  components: {
    TUploader,
  },
  data() {
    return {
      loading: true,
      currentHeaders: null,
      searchExports: ["domain", "object", "policyname"],
      dataSet: {
        fields: [
          { value: "domain", text: this.$t("Domain"), sortable: true },
          { value: "object", text: this.$t("Status"), sortable: true },
          {
            value: "subdomains",
            text: this.$t("Subdomains"),
            sortable: true,
          },
          {
            value: "log_requests",
            text: this.$t("Log Requests"),
            sortable: true,
          },
          {
            value: "comment",
            text: this.$t("Comment"),
            sortable: true,
          },
        ],
        items: [],
      },
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Allow a Domain"),
              value: {
                name: this.$t("Allow a Domain"),
                modal: "t-add-domain",
                type: "allow",
              },
            },
            {
              text: this.$t("Block a Domain"),
              value: {
                name: this.$t("Block a Domain"),
                modal: "t-add-domain",
                type: "block",
              },
            },
          ],
        },
        import: { enabled: true },
        export: { enabled: true },
        delete: { enabled: true },
      },
    };
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("globalAllowList", [
      "fetchGlobalAllowList",
      "createGlobalAllowListEntry",
      "deleteGlobalAllowListEntry",
    ]),
    ...mapActions("fileUpload", ["setDialog", "clearCache"]),
    ...mapActions("globalDomains", [
      "createGlobalDomainListEntry",
      "fetchGlobalDomainList",
      "importGlobalDomains",
    ]),

    ...mapActions("globalBlockList", [
      "fetchGlobalBlockList",
      "createGlobalBlockListEntry",
      "deleteGlobalBlockListEntry",
    ]),
    async submit(value) {
      const action = value.domain.split(" ")[0].toLowerCase();
      const files = value.files;

      try {
        await this.importGlobalDomains({
          action: action,
          file: files,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        await Promise.all([
          this.fetchGlobalAllowList(),
          this.fetchGlobalBlockList(),
        ]);
        this.clearCache();
      }
    },
    async onDelete(items) {
      try {
        await Promise.all(
          items.map((item) =>
            item.object === "allow"
              ? this.deleteGlobalAllowListEntry({
                  allowId: item.id,
                })
              : this.deleteGlobalBlockListEntry({
                  blockId: item.id,
                })
          )
        );
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: items.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    onImport() {
      this.setDialog(true);
    },
    onAdd({ name, modal, type }) {
      this.createModal({
        modal,
        dataTestId: `modal-add-domain-${type}`,
        title: name,
        modalData: {
          isGlobal: true,
          type,
        },
        props: {
          size: "lg",
          okTitle: `${
            type === "allow" ? this.$t("Allow") : this.$t("Block")
          } ${this.$t("Domain")}`,
        },
      });

      this.showModal();
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "domains");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No domains in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onRowClicked(item) {
      this.$router.push({
        path: `/system/global_settings/global_domains/${item.id}?t=${item.object}`,
      });
    },
  },
  computed: {
    ...mapGetters("globalAllowList", ["globalAllowList"]),
    ...mapGetters("globalBlockList", ["globalBlockList"]),
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("fileUpload", ["modal"]),
  },
  watch: {
    globalAllowList() {
      this.dataSet.items = [...this.globalAllowList, ...this.globalBlockList];
    },
    globalBlockList() {
      this.dataSet.items = [...this.globalAllowList, ...this.globalBlockList];
    },
  },
  async created() {
    this.setPageTitle(this.$t("Global Domains"));
    this.setPageSubtitle([
      this.$t(
        "Block or allow access to particular URLs globally by adding or import the domains here."
      ),
    ]);
    await Promise.all([
      this.fetchGlobalAllowList(),
      this.fetchGlobalBlockList(),
    ]);

    this.dataSet.items = [...this.globalAllowList, ...this.globalBlockList];
    this.loading = false;
  },
};
</script>
